import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, tap } from 'rxjs';

import { ChangeUserPasswordRequest } from '@/app/features/users/models/user.model';
import { environment } from '@/environments/environment';
import { LayoutService } from '@/app/core/auth-layout/service/layout.service';


export interface LoginRequest {
    username: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    refresh_expires_in: number;
    user: CurrentUser;
}

export interface RefreshTokenRequest {
    refresh_token: string;
}

export interface CurrentUser {
    id: number;
    external_id: string | null;
    username: string;
    email: string;
    given_name: string;
    family_name: string;
    enabled: boolean;
    roles: string[];
    permissions: string[];
    preferences: UserPreferences;
}

export interface UserPreferences {
    company_id: number | null;
    market_id: number | null;

    language_code: string;
    timezone: string;
    locale: string;

    theme: string;
    color_scheme: string;
    primary: string;
    surface: string | null;

    navigation_collapsed: boolean;
}


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly refreshTokenKey =
        'product-bridge.refresh-token';

    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly layoutService = inject(LayoutService);

    private refreshPromise?: Promise<boolean>;
    private currentUser?: CurrentUser;

    private roleSet = new Set<string>();
    private permissionSet = new Set<string>();

    private accessTokenValue?: string;
    private refreshTokenValue?: string;
    private accessTokenExpiresAt?: number;

    private readonly _authenticated = signal(false);

    readonly authenticatedSignal =
        this._authenticated.asReadonly();


    private readonly _userPreferences =
        signal<UserPreferences | null>(null);

    readonly userPreferences =
        this._userPreferences.asReadonly();

    private initializationPromise?: Promise<void>;


    async initialize(): Promise<void> {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise =
            this.initializeInternal();

        return this.initializationPromise;

    }

    private async initializeInternal(): Promise<void> {
        const refreshToken = localStorage.getItem(
            this.refreshTokenKey,
        );

        if (!refreshToken) {
            this.clearAuthentication();
            return;
        }

        this.refreshTokenValue = refreshToken;

        try {
            const refreshed =
                await this.refreshAccessToken();

            if (!refreshed) {
                this.clearAuthentication();
                return;
            }

            await this.loadCurrentUser();

        } catch (error) {
            console.error(
                'Authentication initialization failed.',
                error,
            );

            this.clearAuthentication();
        }
    }

    async login(request: LoginRequest): Promise<void> {
        const response = await firstValueFrom(
            this.http.post<LoginResponse>(
                '/api/v1/authentication/login',
                request,
            ),
        );



        this.accessTokenValue = response.access_token;
        this.refreshTokenValue = response.refresh_token;
        this.accessTokenExpiresAt =
            Date.now() + response.expires_in * 1000;


        localStorage.setItem(
            this.refreshTokenKey,
            response.refresh_token,
        );

        this._authenticated.set(true);


        this.currentUser = response.user;

        this._userPreferences.set(
            response.user.preferences
        );

        this.applyUserTheme(
            response.user.preferences
        );

        this.roleSet = new Set(response.user.roles);
        this.permissionSet = new Set(response.user.permissions);

        //await this.router.navigate(['/dashboard']);
    }


    async loadCurrentUser(): Promise<CurrentUser> {
        const user = await firstValueFrom(
            this.http.get<CurrentUser>(
                '/api/v1/authentication/me',
            ),
        );

        this.currentUser = user;

        if (!environment.production) {
            (window as any).auth = this.currentUser;
        }

        this._userPreferences.set(
            user.preferences
        );
        this.applyUserTheme(
            user.preferences
        );

        this.roleSet = new Set(user.roles);
        this.permissionSet = new Set(user.permissions);

        this._authenticated.set(true);

        return user;
    }

    private applyUserTheme(preferences: UserPreferences): void {
       this.layoutService.applyPreferences(preferences);
    }

    hasRole(role: string): boolean {
        return this.roleSet.has(role);
    }


    hasAnyRole(...roles: string[]): boolean {
        return roles.some(
            role => this.roleSet.has(role),
        );
    }


    hasPermission(permission: string): boolean {
        return this.permissionSet.has(permission);
    }


    hasAnyPermission(...permissions: string[]): boolean {
        return permissions.some(
            permission => this.permissionSet.has(permission),
        );
    }


    async getAccessToken(): Promise<string | undefined> {
        if (!this.accessTokenValue) {
            return undefined;
        }

        if (!this.accessTokenExpiresAt) {
            return this.accessTokenValue;
        }

        const refreshBeforeMs = 30_000;

        const expiresSoon =
            Date.now() >= this.accessTokenExpiresAt - refreshBeforeMs;

        if (!expiresSoon) {
            return this.accessTokenValue;
        }

        try {
            await this.refreshAccessToken();

            return this.accessTokenValue;
        } catch (error) {
            console.error(
                'Unable to refresh access token.',
                error,
            );

            await this.logout();

            return undefined;
        }
    }


    async refreshAccessToken(): Promise<boolean> {

        if (!this.refreshTokenValue) {
            return false;
        }

        try {
            const response = await firstValueFrom(
                this.http.post<LoginResponse>(
                    '/api/v1/authentication/refresh',
                    {
                        refresh_token:
                            this.refreshTokenValue,
                    },
                ),
            );


            this.accessTokenValue =
                response.access_token;

            this.accessTokenExpiresAt =
                Date.now() +
                response.expires_in * 1000;

            // Ako backend rotira refresh token,
            // spremi novi.
            this.refreshTokenValue =
                response.refresh_token;

            localStorage.setItem(
                this.refreshTokenKey,
                response.refresh_token,
            );

            return true;

        } catch (error) {
            return false;
        }
    }


    private async performRefresh(): Promise<boolean> {
        const refreshToken = this.refreshTokenValue;

        if (!refreshToken) {
            return false;
        }

        const response = await firstValueFrom(
            this.http.post<LoginResponse>(
                '/api/v1/authentication/refresh',
                {
                    refresh_token: refreshToken,
                } satisfies RefreshTokenRequest,
            ),
        );

        this.accessTokenValue = response.access_token;
        this.refreshTokenValue = response.refresh_token;
        this.accessTokenExpiresAt =
            Date.now() + response.expires_in * 1000;

        this._authenticated.set(true);

        if (response.user) {
            this.currentUser = response.user;

            this.roleSet = new Set(response.user.roles);
            this.permissionSet = new Set(response.user.permissions);
        }

        return true;
    }


    async logout(): Promise<void> {
        const refreshToken = this.refreshTokenValue;

        this.clearAuthentication();

        if (!refreshToken) {
            return;
        }

        try {
            await firstValueFrom(
                this.http.post<void>(
                    '/api/v1/authentication/logout',
                    {
                        refresh_token: refreshToken,
                    },
                ),
            );
        } catch (error) {
            console.error(
                'Logout request failed.',
                error,
            );
        }
        this._userPreferences.set(null);
        await this.router.navigate(['/']);
    }


    changeMyPassword(
        request: ChangeUserPasswordRequest,
    ): Observable<void> {

        return this.http.put<void>(
            '/api/v1/authentication/me/password',
            request,
        );
    }

    updateUserPreferences(
        preferences: UserPreferences,
    ): Observable<UserPreferences> {

        return this.http
            .patch<UserPreferences>(
                '/api/v1/me/preferences',
                preferences,
            )
            .pipe(
                tap(updatedPreferences => {
                    this._userPreferences.set(
                        updatedPreferences,
                    );

                    if (this.currentUser) {
                        this.currentUser = {
                            ...this.currentUser,
                            preferences: updatedPreferences,
                        };
                    }
                }),
            );
    }


    private clearAuthentication(): void {
        this._authenticated.set(false);

        this.currentUser = undefined;

        this.roleSet.clear();
        this.permissionSet.clear();

        this.accessTokenValue = undefined;
        this.refreshTokenValue = undefined;
        this.accessTokenExpiresAt = undefined;

        this.refreshPromise = undefined;


        localStorage.removeItem(
            this.refreshTokenKey,
        );

    }


    get accessToken(): string | undefined {
        return this.accessTokenValue;
    }


    get authenticated(): boolean {
        return this._authenticated();
    }


    get username(): string | undefined {
        return this.currentUser?.username;
    }


    get user(): CurrentUser | undefined {
        return this.currentUser;
    }


    get roles(): ReadonlySet<string> {
        return this.roleSet;
    }


    get permissions(): ReadonlySet<string> {
        return this.permissionSet;
    }
}